/**
 * Composite user writes.
 *
 * The rule this module exists to enforce:
 *
 * > Every mutation that touches a user invalidates `['users','with-customers']`
 * > **exactly once, after the last write in its sequence.**
 *
 * Single-write mutations satisfy the rule on their own: `useUsers`'s seven
 * mutations and `useCustomers`'s three user writes each invalidate in their
 * `onSuccess`.
 *
 * The user dialogs are the case the rule was written for. They perform *two*
 * writes per save — an identity write (`POST /users` or `PUT /users/:id`) and
 * then an access write (`PUT /users/:id/properties` for a Property Manager,
 * `PUT /users/:id/customers` for everyone else). Before this helper existed the
 * identity write invalidated in its own `onSuccess`, i.e. *before* the access
 * write had even been issued, so the refetch could complete first and repaint
 * the table with the old customer/role — a save that visibly did nothing until
 * the next unrelated refetch.
 */

export interface SaveIdentityThenAccessArgs<TIdentity> {
  /**
   * Write 1 — the identity write (create or update the user).
   *
   * MUST NOT invalidate the users query itself. Callers pass
   * `useUsers().createUserWithoutInvalidation` /
   * `updateUserWithoutInvalidation`, which are exactly the invalidating
   * mutations' requests minus the `onSuccess`.
   */
  writeIdentity: () => Promise<TIdentity>;

  /**
   * Write 2 — the access write, receiving write 1's result. Callers pass one of
   * `useCustomers`'s writes, which invalidate the users query in their own
   * `onSuccess`. That invalidation is *the* invalidation for the whole
   * sequence: it is emitted after the last write, exactly once.
   */
  writeAccess: (identity: TIdentity) => Promise<unknown>;

  /**
   * Invalidates `['users','with-customers']`
   * (`useCustomers().invalidateUsers`).
   *
   * Called by this helper only when write 2 *fails*, where write 2 emits no
   * invalidation of its own. Invalidating on that path is deliberate: write 1
   * did land, so the cached list is already stale, and showing the saved name /
   * email / enabled flag next to the error beats showing neither. It is never
   * called on the success path, where doing so would double-refetch.
   */
  invalidateUsers?: () => unknown;
}

/**
 * Runs a dialog's identity write and then its access write, guaranteeing that
 * the users query is invalidated exactly once and never before the last write
 * that was attempted.
 *
 * Ordering, in every path:
 *  - write 1 rejects  → write 2 is never issued, nothing is invalidated
 *                       (nothing changed server-side), the error propagates.
 *  - write 2 rejects  → `invalidateUsers()` fires once, then the error
 *                       propagates so the dialog keeps its existing toast and
 *                       error handling.
 *  - both resolve     → write 2's own `onSuccess` invalidation is the single
 *                       invalidation, and it fires after both writes.
 *
 * Returns write 1's result.
 */
export async function saveIdentityThenAccess<TIdentity>({
  writeIdentity,
  writeAccess,
  invalidateUsers,
}: SaveIdentityThenAccessArgs<TIdentity>): Promise<TIdentity> {
  // No try/catch: a failed identity write leaves nothing to refetch.
  const identity = await writeIdentity();

  try {
    await writeAccess(identity);
  } catch (error) {
    // Write 1 landed and write 2 did not invalidate, so do it here — once.
    invalidateUsers?.();
    throw error;
  }

  return identity;
}
